import { create } from "zustand"
import { useRef, FC, useMemo, useEffect } from 'react'
import { Modal } from 'bootstrap'
import { Formik, Form, FormikValues, FormikProps } from "formik"
import * as Yup from 'yup'

import { StatusErrorMessage, FormikTextField, FormikMultiFileUpload } from '../widgets'
import { SubmitButtonRow } from '../layout'

import { cloneDeep } from 'lodash'

import { getErrorMessage, processNoteValues } from "../helpers"
import { IWebFileAttachment } from "../../modules/core/applications"

interface IApplicationTerminateRequestModel {    
  note: string,
  noteAttachments: Array<IWebFileAttachment>,   
  requireAttachment: boolean
}


const initApplicationTerminateRequestValues: IApplicationTerminateRequestModel = {
  note: '',
  noteAttachments: [],  
  requireAttachment: true,
}

const ApplicationTerminateRequestSchema: any = Yup.object({
  note: Yup.string().trim().required().label('Reason'),
  noteAttachments: Yup.array().label('Attachment/s')
  .when('requireAttachment', {
    is: true,
    then: (schema) => schema.min(1, 'Attachment required').required(),
  }),          
  requireAttachment: Yup.boolean(),           
});


const ApplicationTerminateRequestDialogForm: FC<FormikProps<IApplicationTerminateRequestModel>> = ({ values, status }) => {
  
  const { toggleShow, message, requireAttachment } = useApplicationTerminateRequestDialogState(state => state);
  

  return(
  <Form id='kt_application_terminate_request_dialog_form' className='form' noValidate>
    <div className="text-center fs-5">
      {message}
    </div>                
    <div className='d-flex flex-column p-4' >
      <div className="row mb-4">        
        <FormikTextField size="12" label="Reason" name="note" rows={3} required />
      </div>
     
      <div className="row mb-4">        
        <FormikMultiFileUpload size="12" label="Attachment" name="noteAttachments" acceptFiles="ApplicantFileFormats" required={requireAttachment}  />
      </div> 
    
      <StatusErrorMessage status={status} />
      <SubmitButtonRow doClose={() => toggleShow(false)} />
    </div>
  </Form>
  )
}


type ModalNoteProps = {
  title: string
  message: string
  onOK: ((values?:any) => Promise<void>) | undefined
  onCancel?: Function | undefined
  requireAttachment?: boolean
}

interface ApplicationTerminateRequestDialogState {
  title: string
  message: string
  requireAttachment: boolean
  onOK : ((values?:any) => Promise<void>) | undefined
  onCancel: Function | undefined
  show: boolean,
  toggleShow: (show: boolean) => void
  initModal: (params: ModalNoteProps) => void
}

export const useApplicationTerminateRequestDialogState = create<ApplicationTerminateRequestDialogState>()((set, get) => ({
  title: '',
  message: '',
  requireAttachment: true,
  onOK: undefined,
  onCancel: undefined,
  show: false, 
  toggleShow: (show: boolean) => { if(get().show !== show) set({show});},
  initModal: (params: ModalNoteProps) => set({ requireAttachment: true, ...params}),
  clear: () => set({ 
    title: '',
    message: '',
    onOK: undefined,
    show: false, 
  }),
}));


export const ApplicationTerminateRequestDialog =  () => {
        
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { show, onOK, onCancel, requireAttachment, title, toggleShow} = useApplicationTerminateRequestDialogState(state => state);

  const initValues = useMemo(() => {
    return {
      ...cloneDeep(initApplicationTerminateRequestValues),
      requireAttachment,
    };        
  }, [requireAttachment]);
    
  const doSubmit = (values: any, actions: FormikValues) => {        
    const { setStatus, setSubmitting } = actions;
    setStatus('');
    setSubmitting(false);
    if (! onOK) {
      toggleShow(false);
      return;
    } 
    
    setSubmitting(true);

    let payload = {};

    processNoteValues(values, payload);
    
    onOK(payload)        
    .then( (ret: any) => {
      toggleShow(false);
    })
    .catch( err => {
      setStatus(getErrorMessage(err));                         
    })         
    .finally(() => {
      setSubmitting(false);
    })
  }

  useEffect(() => {
    if (! (modalRef && modalRef.current)) return;
  
    const modalEle = modalRef.current;    
    if (show) {
      const bsModal = new Modal(modalEle, {
        backdrop: false,
        keyboard: false
      })
      bsModal.show();
      return;                
    } 
    const bsModal= Modal.getInstance(modalEle)
    if (bsModal) bsModal.hide();     
    if (onCancel) onCancel();                
  // eslint-disable-next-line          
  }, [show, modalRef]);

  return (
  <div className="modal" ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" aria-labelledby="staticBackdropLabel" aria-hidden="true">
    <div className="modal-dialog modal-dialog-centered min-w-600px">
      <div className="modal-content">
        <div className="modal-header">
          <div>
            <div className="text-muted fs-7">Request for Termination of Application</div>
            <h2 className="modal-title fs-5" id="staticBackdropLabel">{title}</h2>
          </div>
          <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => toggleShow(false)}></button>
        </div>

        <div className="modal-body">
          <div className="">   
            <Formik
              initialValues={initValues}
              validationSchema={ApplicationTerminateRequestSchema}
              enableReinitialize={true}
              onSubmit={doSubmit}
              component={ApplicationTerminateRequestDialogForm}
            />
          </div>
        </div>                
      </div>
    </div>
  </div>
  )
};